import { useState, ReactNode, ChangeEvent } from "react";
import { Drawer, Button, TextInput, Checkbox, Row, Tag } from "@narmi/design_system";
import { moneyFormatter } from "./PaymentTable";
import {
  PAYMENT_STATUSES,
  CASE_TYPES,
  PAYMENT_STATUSES_TYPE,
  CASE_TYPES_TYPE,
  PAYMENT_DIRECTIONS,
  PAYMENT_DIRECTIONS_TYPE,
  CASE_STATUSES_TYPE,
  CASE_STATUSES,
} from "./codesets";

export type PaymentQuery = {
  start_date: string | undefined;
  end_date: string | undefined;
  state: Array<string> | undefined;
  from_amount: string | undefined;
  to_amount: string | undefined;
  direction: PAYMENT_DIRECTIONS_TYPE | undefined;
  text: string | undefined;
};

export type CaseQuery = {
  payment_id: string | undefined;
  start_date: string | undefined;
  end_date: string | undefined;
  state: Array<string> | undefined;
  from_amount: string | undefined;
  to_amount: string | undefined;
  kind: Array<string> | undefined;
  status: CASE_STATUSES_TYPE | undefined;
  text: string | undefined;
};

const FilterDialog = ({
  type,
  filterQuery,
  setFilterQuery,
  dialogOpen,
  setDialogOpen,
  applyCallback,
  filterTags,
  setFilterTags,
}: {
  type: "payments" | "cases";
  filterQuery: PaymentQuery | CaseQuery;
  setFilterQuery: CallableFunction;
  dialogOpen: boolean;
  setDialogOpen: CallableFunction;
  applyCallback: CallableFunction;
  filterTags: Array<ReactNode>;
  setFilterTags: CallableFunction;
}) => {
  const [amountError, setAmountError]: [string | null, CallableFunction] = useState(null);
  const [dateError, setDateError]: [string | null, CallableFunction] = useState(null);

  function handlePaymentStatusChange(status: string) {
    const copy = { ...filterQuery };
    if (copy.state === undefined) {
      copy.state = [];
    }
    const checked = copy.state.includes(status);
    if (!checked) {
      copy.state = copy.state.concat([status]);
    } else {
      copy.state.splice(copy.state.indexOf(status), 1);
      setFilterTags(filterTagsForQuery(copy));
    }
    if (copy.state.length === 0) {
      copy.state = undefined;
    }
    setFilterQuery(copy);
  }

  function handleCaseTypeChange(newKind: string) {
    const copy = { ...(filterQuery as CaseQuery) };
    if (copy.kind === undefined) {
      copy.kind = [];
    }
    const checked = copy.kind.includes(newKind);
    if (!checked) {
      copy.kind = copy.kind.concat([newKind]);
    } else {
      copy.kind.splice(copy.kind.indexOf(newKind), 1);
      setFilterTags(filterTagsForQuery(copy));
    }
    if (copy.kind.length === 0) {
      copy.kind = undefined;
    }
    setFilterQuery(copy);
  }

  function filterTagsForQuery(q: CaseQuery | PaymentQuery) {
    function tagRow(label: string, onDismiss: CallableFunction) {
      return (
        <Row.Item shrink>
          <Tag
            kind="outline"
            dismissible
            label={label}
            onDismiss={() => {
              onDismiss();
            }}
            key={label}
          />
        </Row.Item>
      );
    }
    const tags: Array<ReactNode> = [];
    // State tags
    for (const s of q.state || []) {
      tags.push(
        tagRow(PAYMENT_STATUSES[s as PAYMENT_STATUSES_TYPE], () => {
          handlePaymentStatusChange(s);
        })
      );
    }
    // Case type tags
    if (type === "cases") {
      for (const k of (q as CaseQuery).kind || []) {
        tags.push(
          tagRow(CASE_TYPES[k as CASE_TYPES_TYPE], () => {
            handleCaseTypeChange(k);
          })
        );
      }
    }
    // Date tags
    if (q.start_date !== undefined || q.end_date !== undefined) {
      const startDt = q.start_date ? new Date(q.start_date) : null;
      const endDt = q.end_date ? new Date(q.end_date) : null;
      if (endDt) {
        // Display correct inputted date, but search for +1 day so end date is inclusive
        endDt.setDate(endDt.getDate() - 1);
      }
      if (startDt && endDt) {
        tags.push(
          tagRow(`${startDt.toLocaleDateString()} - ${endDt.toLocaleDateString()}`, () => {
            const copy = { ...filterQuery };
            copy.start_date = undefined;
            copy.end_date = undefined;
            setFilterQuery(copy);
            setFilterTags(filterTagsForQuery(copy));
          })
        );
      } else if (startDt) {
        tags.push(
          tagRow(`> ${startDt.toLocaleDateString()}`, () => {
            const copy = { ...filterQuery };
            copy.start_date = undefined;
            setFilterQuery(copy);
            setFilterTags(filterTagsForQuery(copy));
          })
        );
      } else if (endDt) {
        tags.push(
          tagRow(`< ${endDt.toLocaleDateString()}`, () => {
            const copy = { ...filterQuery };
            copy.end_date = undefined;
            setFilterQuery(copy);
            setFilterTags(filterTagsForQuery(copy));
          })
        );
      }
    }
    // Amount tags
    if (q.from_amount !== undefined || q.to_amount !== undefined) {
      if (q.from_amount && q.to_amount) {
        tags.push(
          tagRow(
            `${moneyFormatter.format(
              Number(q.from_amount)
            )} - ${moneyFormatter.format(Number(q.to_amount))}`,
            () => {
              const copy = { ...filterQuery };
              copy.from_amount = undefined;
              copy.to_amount = undefined;
              setFilterQuery(copy);
              setFilterTags(filterTagsForQuery(copy));
            }
          )
        );
      } else if (q.from_amount) {
        tags.push(
          tagRow(`> ${moneyFormatter.format(Number(q.from_amount))}`, () => {
            const copy = { ...filterQuery };
            copy.from_amount = undefined;
            setFilterQuery(copy);
            setFilterTags(filterTagsForQuery(copy));
          })
        );
      } else if (q.to_amount) {
        tags.push(
          tagRow(`< ${moneyFormatter.format(Number(q.to_amount))}`, () => {
            const copy = { ...filterQuery };
            copy.to_amount = undefined;
            setFilterQuery(copy);
            setFilterTags(filterTagsForQuery(copy));
          })
        );
      }
    }
    // Direction Tag
    if ("direction" in q && q.direction) {
      tags.push(
        tagRow(PAYMENT_DIRECTIONS[q.direction], () => {
          const copy = { ...filterQuery };
          (copy as PaymentQuery).direction = undefined;
          setFilterQuery(copy);
          setFilterTags(filterTagsForQuery(copy));
        })
      );
    }
    // Open/closed case tags
    if ("status" in q && q.status) {
      tags.push(
        tagRow(CASE_STATUSES[q.status], () => {
          const copy = { ...(filterQuery as CaseQuery) };
          copy.status = undefined;
          setFilterQuery(copy);
          setFilterTags(filterTagsForQuery(copy));
        })
      );
    }
    return tags;
  }

  function handleSubmit() {
    setAmountError(null);
    setDateError(null);
    if (Number(filterQuery.from_amount) < 0 || Number(filterQuery.to_amount) < 0) {
      setAmountError("Amount filter cannot be negative.");
      return;
    }
    if (Number(filterQuery.from_amount) > Number(filterQuery.to_amount)) {
      setAmountError("Amount lower bound cannot be greater than upper bound.");
      return;
    }
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);

    if (
      (filterQuery.start_date && new Date(filterQuery.start_date) > tomorrow) ||
      (filterQuery.end_date && new Date(filterQuery.end_date) > tomorrow)
    ) {
      setDateError("Search date cannot be in the future.");
      return;
    }
    if (
      filterQuery.start_date &&
      filterQuery.end_date &&
      new Date(filterQuery.end_date) < new Date(filterQuery.start_date)
    ) {
      setDateError("Invalid date range, start date must be before end date.");
      return;
    }
    setFilterTags(filterTagsForQuery(filterQuery));
    applyCallback();
    setDialogOpen(false);
  }

  function handleDateChange(field: keyof PaymentQuery | keyof CaseQuery, date: string) {
    const copy = { ...filterQuery };
    const dt = new Date(date);
    dt.setHours(dt.getHours() + dt.getTimezoneOffset() / 60);
    if (field === "end_date") {
      // Add 1 day so end date is inclusive
      dt.setDate(dt.getDate() + 1);
    }
    // @ts-expect-error Upset even tho using keyof so ignoring
    copy[field] = dt.toISOString();
    setFilterQuery(copy);
    setFilterTags(filterTagsForQuery(copy));
  }

  function handleQueryValueChange(key: keyof PaymentQuery | keyof CaseQuery, value: string) {
    const copy = { ...filterQuery };
    // @ts-expect-error Upset even tho using keyof so ignoring
    copy[key] = value;
    setFilterQuery(copy);
    setFilterTags(filterTagsForQuery(copy));
  }

  return (
    <>
      <div className={"padding--top--s"} style={{ width: "100%" }}>
        <Row>{filterTags}</Row>
      </div>
      <Drawer
        isOpen={dialogOpen}
        depth="30%"
        showControls={false}
        showClose={false}
        onUserDismiss={() => setDialogOpen(false)}
      >
        <h4 className="fontFamily--body padding--bottom--s">{`Filter ${type}`}</h4>
        <div>
          {type === "cases" && <p className="fontWeight--bold margin--bottom--xs">Case status</p>}
          {type === "cases" &&
            Object.keys(CASE_STATUSES).map((s) => (
              <div className="margin--bottom--s">
                <Checkbox
                  checked={
                    (filterQuery as CaseQuery).status === undefined ||
                    (filterQuery as CaseQuery).status === s
                  }
                  label={CASE_STATUSES[s as CASE_STATUSES_TYPE]}
                  name={s}
                  key={s}
                  onChange={() => {
                    let newStatus: "open" | "closed" | undefined;
                    if ((filterQuery as CaseQuery).status === undefined) {
                      newStatus = s === "open" ? "closed" : "open";
                    } else {
                      newStatus = undefined;
                    }
                    const copy = { ...filterQuery };
                    (copy as CaseQuery).status = newStatus;
                    setFilterQuery(copy);
                  }}
                />
              </div>
            ))}
          {type === "cases" && <p className="fontWeight--bold margin--bottom--xs">Type</p>}
          {type === "cases" && (
            <div>
              {Object.keys(CASE_TYPES).map((t) => (
                <Row>
                  <div className="margin--bottom--s">
                    <Checkbox
                      checked={!!(filterQuery as CaseQuery).kind?.includes(t)}
                      label={CASE_TYPES[t as CASE_TYPES_TYPE]}
                      name={t}
                      onChange={() => handleCaseTypeChange(t)}
                      key={t}
                    />
                  </div>
                </Row>
              ))}
            </div>
          )}
          {type === "payments" && (
            <div>
              <p className="fontWeight--bold margin--bottom--xs">Payment status</p>
              {Object.keys(PAYMENT_STATUSES)
                .sort()
                .map((s) => {
                  return (
                    <Row>
                      <div className="margin--bottom--s">
                        <Checkbox
                          checked={!!filterQuery.state?.includes(s)}
                          label={PAYMENT_STATUSES[s as PAYMENT_STATUSES_TYPE]}
                          name={s}
                          onChange={() => handlePaymentStatusChange(s)}
                          key={s}
                        />
                      </div>
                    </Row>
                  );
                })}
            </div>
          )}
          {type === "payments" && <p className="fontWeight--bold margin--bottom--xs">Direction</p>}
          {type === "payments" &&
            Object.keys(PAYMENT_DIRECTIONS).map((dir) => (
              <div className="margin--bottom--s">
                <Checkbox
                  checked={
                    (filterQuery as PaymentQuery).direction === undefined ||
                    (filterQuery as PaymentQuery).direction === dir
                  }
                  label={PAYMENT_DIRECTIONS[dir as PAYMENT_DIRECTIONS_TYPE]}
                  name={dir}
                  key={dir}
                  onChange={() => {
                    let newDirection: PAYMENT_DIRECTIONS_TYPE | undefined;
                    if ((filterQuery as PaymentQuery).direction === undefined) {
                      newDirection = dir === "send" ? "receive" : "send";
                    } else {
                      newDirection = undefined;
                    }
                    const copy = { ...filterQuery };
                    (copy as PaymentQuery).direction = newDirection;
                    setFilterQuery(copy);
                  }}
                />
              </div>
            ))}
          <p className="fontWeight--bold margin--top margin--bottom--xs">Last updated</p>
          <div className="padding--bottom">
            <Row>
              <Row.Item>
                <TextInput
                  className="filterDialogInput"
                  label="Start date"
                  type="date"
                  error={dateError}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => {
                    handleDateChange("start_date", e.target.value);
                  }}
                />
              </Row.Item>
              <Row.Item>
                <TextInput
                  className="filterDialogInput"
                  label="End date"
                  type="date"
                  onChange={(e: ChangeEvent<HTMLInputElement>) => {
                    handleDateChange("end_date", e.target.value);
                  }}
                />
              </Row.Item>
            </Row>
          </div>
          {type === "payments" && <p className="fontWeight--bold margin--bottom--xs">Amount</p>}
          {type === "payments" && (
            <div className="padding--bottom">
              <Row>
                <Row.Item>
                  <TextInput
                    label="Minimum amount"
                    type="number" // ignore-danger-type-number
                    step="any"
                    inputMode="decimal"
                    error={amountError}
                    startContent={
                      <p
                        className={
                          filterQuery.from_amount
                            ? "margin--top--s filterDialogInput"
                            : "filterDialogInput"
                        }
                      >
                        {filterQuery.from_amount ? "$" : ""}
                      </p>
                    }
                    defaultValue={filterQuery.from_amount}
                    value={filterQuery.from_amount}
                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                      handleQueryValueChange("from_amount", e.target.value)
                    }
                  />
                </Row.Item>
                <Row.Item>
                  <TextInput
                    label="Maximum amount"
                    type="number" // ignore-danger-type-number
                    step="any"
                    inputMode="decimal"
                    startContent={
                      <p
                        className={
                          filterQuery.to_amount
                            ? "margin--top--s filterDialogInput"
                            : "filterDialogInput"
                        }
                      >
                        {filterQuery.from_amount ? "$" : ""}
                      </p>
                    }
                    defaultValue={filterQuery.to_amount}
                    value={filterQuery.to_amount}
                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                      handleQueryValueChange("to_amount", e.target.value)
                    }
                  />
                </Row.Item>
              </Row>
            </div>
          )}
        </div>
        <div>
          <Row>
            <Row.Item>
              <Button
                label="Clear all"
                kind="plain"
                onClick={() => {
                  setFilterQuery({});
                  setAmountError(null);
                  setDateError(null);
                  setFilterTags(filterTagsForQuery({} as CaseQuery));
                  // hacky way to clear the inputs since TextInput is not fully controlled
                  for (const label of [
                    "Start date",
                    "End date",
                    "Minimum amount",
                    "Maximum amount",
                  ]) {
                    (
                      document.querySelectorAll(
                        `input[label="${label}"]`
                      ) as NodeListOf<HTMLInputElement>
                    ).forEach((e) => (e.value = ""));
                  }
                }}
              />
            </Row.Item>
            <Row.Item shrink>
              <Button
                label="Apply"
                onClick={() => {
                  handleSubmit();
                }}
              />
            </Row.Item>
          </Row>
        </div>
      </Drawer>
    </>
  );
};

export default FilterDialog;
